import { toVideoEmbed } from '../components/recordings/videoEmbed';

/**
 * The length of a linked lesson, asked of the site that hosts it.
 *
 * Done in the browser, and deliberately not through `apiClient`: these are
 * other sites, and the teacher's token must never travel to them. Neither
 * lookup needs a key, which is why only these two providers are covered —
 * Drive, Zoom and an institute's own server publish no length without signed
 * credentials, so those lessons are saved without one.
 *
 * Every failure is null, never an error: a length is a convenience, and a
 * lookup that cannot answer must not stand between a teacher and posting —
 * the lesson is saved without one.
 */

/** Long enough for a slow school connection to load the player once. */
const LOOKUP_TIMEOUT_MS = 10_000;

/** Seconds, or null when the provider is not one we can ask or did not answer. */
export async function readLinkDuration(
  videoUrl: string,
  signal?: AbortSignal,
): Promise<number | null> {
  const { embedUrl, provider } = toVideoEmbed(videoUrl);
  if (!embedUrl) return null;

  if (provider === 'Vimeo') return readVimeoDuration(videoUrl, signal);
  if (provider === 'YouTube') return readYouTubeDuration(embedUrl, signal);
  return null;
}

/** Vimeo's public oEmbed answer carries `duration` in seconds, and allows any origin. */
async function readVimeoDuration(videoUrl: string, signal?: AbortSignal): Promise<number | null> {
  // Saving waits on this, so it gives up on its own as well as when asked to.
  const lookup = new AbortController();
  const timeout = window.setTimeout(() => lookup.abort(), LOOKUP_TIMEOUT_MS);
  signal?.addEventListener('abort', () => lookup.abort());

  try {
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}`,
      { signal: lookup.signal, credentials: 'omit' },
    );
    if (!response.ok) return null;

    const body: unknown = await response.json();
    const duration =
      typeof body === 'object' && body !== null && 'duration' in body ? body.duration : null;
    return typeof duration === 'number' && duration > 0 ? Math.round(duration) : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
}

/**
 * YouTube publishes no length without an API key, but its embedded player
 * reports one to the page that frames it.
 *
 * So a hidden player is framed for a moment and asked — the same `postMessage`
 * conversation YouTube's own IFrame API holds, without loading that script into
 * the page. The parent says "listening" until the player answers; the player's
 * info deliveries carry `duration` once the video's details have loaded. The
 * frame is muted, never played, and removed the moment it has answered.
 */
function readYouTubeDuration(embedUrl: string, signal?: AbortSignal): Promise<number | null> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(null);
      return;
    }

    const src = new URL(embedUrl);
    src.searchParams.set('enablejsapi', '1');
    src.searchParams.set('origin', window.location.origin);
    src.searchParams.set('mute', '1');

    const frame = document.createElement('iframe');
    frame.src = src.toString();
    frame.title = 'Reading the video length';
    frame.className = 'sr-only';
    frame.tabIndex = -1;
    frame.setAttribute('aria-hidden', 'true');

    let poll: number | undefined;
    let timeout: number | undefined;

    const finish = (seconds: number | null) => {
      window.clearInterval(poll);
      window.clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
      signal?.removeEventListener('abort', onAbort);
      frame.remove();
      resolve(seconds);
    };
    const onAbort = () => finish(null);

    function onMessage(event: MessageEvent) {
      if (event.source !== frame.contentWindow || event.origin !== src.origin) return;
      // The player has heard us; it keeps reporting on its own from here.
      window.clearInterval(poll);

      let payload: unknown;
      try {
        payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }

      const info =
        typeof payload === 'object' && payload !== null && 'info' in payload ? payload.info : null;
      const duration =
        typeof info === 'object' && info !== null && 'duration' in info ? info.duration : null;
      if (typeof duration === 'number' && duration > 0) finish(Math.round(duration));
    }

    window.addEventListener('message', onMessage);
    signal?.addEventListener('abort', onAbort);
    frame.addEventListener('load', () => {
      const listening = JSON.stringify({ event: 'listening', id: 1, channel: 'widget' });
      poll = window.setInterval(() => frame.contentWindow?.postMessage(listening, src.origin), 250);
    });
    timeout = window.setTimeout(() => finish(null), LOOKUP_TIMEOUT_MS);

    document.body.append(frame);
  });
}
